<template>
    <el-container style="height: calc(100vh - 80px)" class="bg-white">
        <el-header style="padding: 0px; height: auto; ">
            <!-- title部 -->
            <div class="h-12 p-4 shadow-md">個人設定</div>
        </el-header>

        <!-- 主显示部 -->
        <el-main style="padding-block: 0px; margin-top: 20px;" v-loading="loading">
            <div class="border-b border-b-gray-300 pb-5">
                <span>情報変更</span>
                <div class="flex flex-col items-center">
                    <el-form :model="infoForm" :rules="infoRules" ref="infoFormRef" label-width="150px" style="width: 500px;">
                        <el-form-item label="ユーザーコード" prop="user_code">
                            <el-input v-model="infoForm.user_code" disabled
                                placeholder="ユーザーコードを入力" />
                        </el-form-item>

                        <el-form-item label="ユーザー名" prop="user_name">
                            <el-input v-model="infoForm.user_name" placeholder="ユーザー名を入力" />
                        </el-form-item>

                        <el-form-item label="所属契約会社" prop="company_code">
                            <CompanySelector v-model="infoForm.company_code" disabled/>
                        </el-form-item>

                        <el-form-item label="電話番号" prop="telephone">
                            <el-input v-model="infoForm.telephone" placeholder="電話番号を入力" />
                        </el-form-item>

                        <div class="flex justify-center">
                            <el-button type="primary" class="w-80" @click="handleChangeInfo(infoFormRef)">情報変更</el-button>
                        </div>
                    </el-form>
                </div>
            </div>
            
            <div class="py-5">
                <span>パスワード変更</span>
                <div class="flex flex-col items-center">
                    <el-form :model="passwordForm" :rules="passwordRules" ref="passwordFormRef" label-width="200px" style="width: 500px;">
                        <el-form-item label="現在のパスワード:" prop="current_password">
                            <el-input show-password v-model="passwordForm.current_password" placeholder="現在のパスワード"
                                autocomplete="off" />
                        </el-form-item>

                        <el-form-item label="新しいパスワード:" prop="password">
                            <el-input show-password v-model="passwordForm.password" placeholder="新しいパスワード" autocomplete="off" />
                        </el-form-item>

                        <el-form-item label="新しいパスワード（確認）:" prop="password_confirmation">
                            <el-input show-password v-model="passwordForm.password_confirmation" placeholder="新しいパスワード（確認）"
                                autocomplete="off" />
                        </el-form-item>

                        <div class="flex justify-center">
                            <el-button type="primary" class="w-80" @click="handleChangePassword(passwordFormRef)">パスワード変更</el-button>
                        </div>
                    </el-form>
                </div>
            </div>
        </el-main>

        <el-footer style="height: 20px;"> </el-footer>
    </el-container>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from 'vue';
import { ElMessage, FormInstance, FormRules } from 'element-plus';
import { ErrorCode, ResponseData, ResponseStatus } from '@/components/common/response';
import axios, { AxiosResponse } from 'axios';
import { UserInfo } from '@/components/models/userinfo';
import { getErrorMessage } from '@/components/common/errorMessage';
import { authService } from '@/services/authService';
import CompanySelector from '@/components/common/company-selector.vue';

export default defineComponent({
    name: 'PersonalSetting',
    components: {
        CompanySelector,
    },
    setup() {
        const loginUserCode = authService.getUserInfo().user_code ?? '';

        const info = ref<UserInfo>({
            user_code: '',
            user_name: '',
            telephone: '',
            company_code: '',
            company_info: {},
            password: '',
            site_info: [],
            user_role: '',
            is_active: true,
        });
        
        const infoForm = ref<UserInfo>({
            user_code: '',
            user_name: '',
            telephone: '',
            company_code: '',
            company_info: {},
            password: '',
            site_info: [],
            user_role: '',
            is_active: true,
        });

        const loading = ref<boolean>(true);
        const infoFormRef = ref<FormInstance>()

        const infoRules = ref<FormRules>({
            user_code: [
                { required: true, message: 'ユーザーコードを入力してください', trigger: 'blur' },
                { min: 1, max: 10, message: 'ユーザーコードは1〜10文字の間で入力してください', trigger: 'blur' },
                { pattern: /^[A-Za-z0-9]+$/, message: 'ユーザーコードは半角英数字で入力してください', trigger: 'blur' }
            ],
            user_name: [
                { required: true, message: 'ユーザー名を入力してください', trigger: 'blur' },
                { min: 2, max: 25, message: 'ユーザー名は2〜25文字の間で入力してください', trigger: 'blur' },
                { pattern: /^[^<>\\/|&;$#%@"'{}()[\]]+$/, message: 'ユーザー名に使用できない文字が含まれています', trigger: 'blur' }
            ],
            company_code: [
                { required: true, message: '契約会社を選択してください', trigger: 'blur' }
            ],
            telephone: [
                { required: true, message: '電話番号を入力してください', trigger: 'blur' },
                { pattern: /^[0-9]{10,13}$/, message: '電話番号は10〜13桁の数字で入力してください', trigger: 'blur' }
            ],
        })

        const passwordForm = ref({
            current_password: '',
            password: '',
            password_confirmation: ''
        });

        const passwordFormRef = ref<FormInstance>()

        const passwordRules = ref<FormRules>({
            current_password: [
                { required: true, message: '現在のパスワードを入力してください', trigger: 'blur' },
                { min: 6, max: 20, message: '現在のパスワードは6〜20文字の間で入力してください', trigger: 'blur' },
                {
                    validator: (rule, value, callback) => {
                        if (value !== info.value.password) {
                            callback(new Error('現在のパスワードが不正です'));
                        } else {
                            callback();
                        }
                    },
                    trigger: 'blur'
                }
            ],
            password: [
                { required: true, message: '新しいパスワードを入力してください', trigger: 'blur' },
                { min: 6, max: 20, message: '新しいパスワードは6〜20文字の間で入力してください', trigger: 'blur' },
            ],
            password_confirmation: [
                { required: true, message: '新しいパスワード（確認）を入力してください', trigger: 'blur' },
                { min: 6, max: 20, message: '新しいパスワード（確認）は6〜20文字の間で入力してください', trigger: 'blur' },
                {
                    validator: (rule, value, callback) => {
                        if (value !== passwordForm.value.password) {
                            callback(new Error('新しいパスワードと確認用パスワードが一致しません'));
                        } else {
                            callback();
                        }
                    },
                    trigger: 'blur'
                }
            ],
        })

        onMounted(() => {
            getUser();
        });


        const handleChangePassword = async (formEl: FormInstance | undefined) => {
            if (!formEl) return
            await formEl.validate((valid) => {
                if (valid) {
                    const v:UserInfo = {
                        user_code: info.value.user_code,
                        user_name: info.value.user_name,
                        password: passwordForm.value.password_confirmation,
                        telephone: info.value.telephone,
                        company_code: info.value.company_code,
                        company_info: info.value.company_info,
                        site_info: info.value.site_info,
                        user_role: info.value.user_role,
                        is_active: info.value.is_active,
                    };

                    updateUser(v);
                }
            })
        }

        const handleChangeInfo = async (formEl: FormInstance | undefined) => {
            if (!formEl) return
            await formEl.validate((valid) => {
                if (valid) {
                    updateUser(infoForm.value);
                }
            })
        }

        const updateUser = async (row: UserInfo) => {
            try {
                loading.value = true;
                const res: AxiosResponse<ResponseData<UserInfo>> = await axios.put(`/api/users/${row.user_code}`, row);
                if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                    ElMessage.success(res.data.message);
                } else {
                    ElMessage.error(res.data.message);
                }
                getUser();
            } catch (error) {
                ElMessage.error(getErrorMessage(error as Error));
            } finally {
                // 无论成功还是失败，最后都要关闭loading
                loading.value = false;
            }
        }

        const getUser = async () => {
            try {
                loading.value = true;
                const res: AxiosResponse<ResponseData<UserInfo>> = await axios.get(`/api/users/${loginUserCode}`);
                if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                    const { data } = res.data;

                    info.value = {
                        user_code: data!.user_code,
                        user_name: data!.user_name,
                        password: data!.password,
                        telephone: data!.telephone,
                        company_code: data!.company_code,
                        company_info: data!.company_info,
                        site_info: data!.site_info,
                        user_role: data!.user_role,
                        is_active: data!.is_active,
                    };

                    infoForm.value = {
                        user_code: data!.user_code,
                        user_name: data!.user_name,
                        password: data!.password,
                        telephone: data!.telephone,
                        company_code: data!.company_code,
                        company_info: data!.company_info,
                        site_info: data!.site_info,
                        user_role: data!.user_role,
                        is_active: data!.is_active,
                    };

                    passwordForm.value = {
                        current_password: '',
                        password: '',
                        password_confirmation: ''
                    }

                    // 清除 el-form-item 的校验状态（包括 blur 触发的错误）
                    infoFormRef.value?.clearValidate();
                    passwordFormRef.value?.clearValidate();

                } else {
                    ElMessage.error(res.data.message);
                }

            } catch (error) {
                ElMessage.error(getErrorMessage(error as Error));
            } finally {
                // 无论成功还是失败，最后都要关闭loading
                loading.value = false;
            }
        };

        return {
            handleChangePassword,
            handleChangeInfo,
            infoForm,
            infoFormRef,
            infoRules,
            passwordForm,
            passwordFormRef,
            passwordRules,
            loading,
        }

    }
});
</script>